import React, { useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { isLocal } from '../../utils/isLocal';
import { useDeviceSearch, DeviceSearchParams } from '../../hooks/useDeviceSearch';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DeviceSearchFacet } from '../../types';
import { readStickyTab } from './tabConfig';
import {
  FACET_FILTERS,
  PAGE_URL_WRITE,
  filtersFromParams,
  isPageUrlWrite,
  readSavedFilters,
  restoredView,
  writeSavedFilters,
} from './deviceIndexFilters';
import DecommissionedBadge from '../DecommissionedBadge';
import { searchRowClipLabel } from '../../utils/clipLabel';
import {
  PAGE_SIZES,
  readDeviceListView,
  workspaceHref,
} from '../../utils/deviceListContext';
import {
  Combobox,
  ComboboxInlineAnchor,
  ComboboxInlineContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '../pilot-ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';

/**
 * Plan 94 — Fleet Explorer for `/devices`.
 *
 * Dense operational inventory backed by GET /devices/search: server-side scope
 * filtering, facets, free-text, and page-number pagination. Each row shows both
 * context axes (responsibility entity_path + MyFluxHive app registration) and
 * opens the device workspace. Search/facet/page state lives in the URL; the
 * facet filters are also remembered per account (FS-1075,
 * deviceIndexFilters.ts).
 */

// FS-633: the element id aria-activedescendant points at. Stable per device so
// the highlight survives a re-render of the same page of results.
const rowDomId = (deviceId: number) => `device-row-${deviceId}`;

/**
 * FS-1075 — the Devices page as routed. A URL that arrives with no view gets
 * the account's saved filters before the list mounts, so the first search is
 * already the filtered one.
 */
const DeviceIndex: React.FC = () => {
  const { id: userId } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // The account the view on screen belongs to. Moved after commit rather than
  // during render, so a render React repeats still sees the switch.
  const viewOwner = useRef(userId);
  const accountSwitched = viewOwner.current !== userId;
  useEffect(() => {
    viewOwner.current = userId;
  }, [userId]);

  const next = restoredView(searchParams, readSavedFilters(userId), {
    pageWrote: isPageUrlWrite(location.state),
    accountSwitched,
  });
  if (next) {
    return <Navigate replace to={{ pathname: location.pathname, search: `?${next.toString()}` }} state={PAGE_URL_WRITE} />;
  }
  return <DeviceIndexView key={userId} userId={userId} />;
};

const DeviceIndexView: React.FC<{ userId: number }> = ({ userId }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [qInput, setQInput] = useState(searchParams.get('q') ?? '');
  const debouncedQ = useDebouncedValue(qInput, 300);

  // Push the debounced query into the URL (source of truth for the fetch) and
  // reset to page 1 — but ONLY when the query text actually changes. Without the
  // ref guard this effect re-runs on every render (setSearchParams isn't a stable
  // identity) and would wipe the `page` param back to 1, breaking pagination.
  const prevQ = useRef(searchParams.get('q') ?? '');
  useEffect(() => {
    if (debouncedQ === prevQ.current) return;
    prevQ.current = debouncedQ;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedQ) next.set('q', debouncedQ);
        else next.delete('q');
        next.delete('page');
        return next;
      },
      { replace: true, state: PAGE_URL_WRITE },
    );
  }, [debouncedQ, setSearchParams]);

  // View state (page, size, sort allowlist, filters) is read straight from the
  // URL by the shared reader — the same one the device workspace uses to
  // resolve the list it was opened from, so both read a URL identically.
  const { params, page, pageSize, sort, dir } = readDeviceListView(searchParams);

  const { rows, rowsCurrent, facets, total, loading, error, pageCount } = useDeviceSearch(params);

  const setParam = (key: string, value: string | null, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (resetPage) next.delete('page');
      return next;
    }, { state: PAGE_URL_WRITE });
  };
  // FS-1075: the filters left on screen after the operator chooses or clears
  // one are the ones the next visit starts with. A link that arrives with its
  // own view is shown but not saved.
  const setFilter = (key: keyof DeviceSearchParams, value: string | null) => {
    const filters = filtersFromParams(searchParams);
    if (value) filters[key as string] = value;
    else delete filters[key as string];
    writeSavedFilters(userId, filters);
    setParam(key as string, value);
  };
  const goToPage = (p: number) => setParam('page', String(Math.min(Math.max(p, 1), pageCount)), false);

  // Activated-date header cycles: unsorted → desc (most recent first) → asc →
  // back to the default id order. Resets to page 1 on any change.
  const toggleActivatedSort = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('sort') !== 'activated_at') {
        next.set('sort', 'activated_at');
        next.set('dir', 'desc');
      } else if (next.get('dir') !== 'asc') {
        next.set('dir', 'asc');
      } else {
        next.delete('sort');
        next.delete('dir');
      }
      next.delete('page');
      return next;
    }, { state: PAGE_URL_WRITE });
  };
  const activatedSorted = sort === 'activated_at';
  // Accessible name announces the current sort state AND what the next activation
  // does (the header cycles desc → asc → off), so SR users aren't relying on the
  // aria-hidden glyph. Pairs with aria-sort on the <th>.
  const activatedSortLabel = !activatedSorted
    ? 'Activation date. Activate to sort newest first.'
    : dir === 'desc'
      ? 'Activation date, sorted newest first. Activate to sort oldest first.'
      : 'Activation date, sorted oldest first. Activate to clear sorting.';
  const activatedAriaSort: 'none' | 'ascending' | 'descending' = !activatedSorted
    ? 'none'
    : dir === 'asc'
      ? 'ascending'
      : 'descending';

  const activeFilters = FACET_FILTERS.filter((f) => searchParams.get(f.key as string));

  // Plain click navigates in place; Cmd/Ctrl-click or middle-click opens the
  // device workspace in a new tab (the rows aren't anchors, so emulate it).
  //
  // FS-542: the workspace is opened with this list carried on the URL — the
  // query string as it stands plus the device's position in it — so the back
  // arrow returns to this exact view and Previous/Next step through it.
  //
  // FS-633: takes a plain `newTab` flag rather than the mouse event, so the
  // keyboard path opens a device through this exact function — same URL, same
  // carried list context — instead of reimplementing it.
  const openDevice = (id: number, index: number, newTab: boolean) => {
    const url = workspaceHref(id, readStickyTab(), { query: searchParams.toString(), index });
    if (newTab) {
      window.open(url, '_blank', 'noopener');
    } else {
      navigate(url);
    }
  };

  // Position of a row within the whole list, not just the page on screen —
  // what the workspace carries so Previous/Next can walk across page edges.
  const listIndex = (rowIndex: number) => (page - 1) * pageSize + rowIndex;

  // FS-633 — combobox-style keyboard selection. The search input keeps focus
  // throughout; this is the row Enter would open.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // The highlight goes back to the first row whenever the RESULT SET changes —
  // new query text, a filter chip, a facet, a sort, a page. Keying it to the
  // view (the URL) plus the ids that came back, rather than to the rows array
  // identity, means a refetch that returns the same page leaves the operator's
  // position alone, while anything that actually changes the list resets it.
  const resultKey = `${searchParams.toString()}|${rows.map((r) => r.id).join(',')}`;
  useEffect(() => {
    setSelectedId(rows[0]?.id ?? null);
  }, [resultKey]);

  // Clamped on the way out: a render between a result change and the effect
  // above can otherwise index past the end of a shorter page.
  const highlightedIndex = rows.findIndex((row) => row.id === selectedId);
  const selectedIndex = rows.length === 0 ? -1 : Math.max(highlightedIndex, 0);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : undefined;

  // Keep the highlight on screen when it walks past the fold. jsdom has no
  // layout and no scrollIntoView, hence the optional call.
  useEffect(() => {
    if (!selectedRow) return;
    document.getElementById(rowDomId(selectedRow.id))?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedRow]);

  // Whether the rows on screen answer the text in the box. The search is
  // debounced 300 ms before it reaches the URL, and useDeviceSearch keeps the
  // PREVIOUS page visible while the next one is in flight — so for about half a
  // second after typing, a highlighted row belongs to the old result set.
  // Opening it would take the operator somewhere they did not ask for, which is
  // exactly the type-then-Enter flow this is for. The URL holds the debounced
  // value, so comparing it to the raw input covers both the debounce window and
  // the fetch that follows.
  const resultsMatchInput = rowsCurrent && !loading && !error && qInput === (searchParams.get('q') ?? '');
  const suggestionRows = resultsMatchInput ? rows : [];

  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="h-full w-full bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-white text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}
      <div className="max-w-7xl mx-auto p-2">
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-2xl font-bold">Devices</h2>
            <div className="w-full max-w-md">
              <label htmlFor="device-search" className="mb-1 block text-sm font-medium">Search devices</label>
              <p id="device-search-help" className="mb-1 text-xs text-muted-foreground">
                Search serial, ID, description, owner, email, location or path. Results stay in server order.
              </p>
              <Combobox<string>
                items={suggestionRows.map((row) => String(row.id))}
                filter={null}
                modal={false}
                open={searchOpen}
                onOpenChange={setSearchOpen}
                inputValue={qInput}
                onInputValueChange={(value, details) => {
                  if (details.reason !== 'item-press') setQInput(value);
                }}
                value={null}
                autoHighlight
                onItemHighlighted={(id) => {
                  if (suggestionRows.some((row) => String(row.id) === id)) setSelectedId(id === undefined ? null : Number(id));
                }}
                onValueChange={(id, details) => {
                  if (id === null) return;
                  const index = suggestionRows.findIndex((row) => String(row.id) === id);
                  if (index < 0) return;
                  const event = details.event;
                  const newTab = event instanceof KeyboardEvent && (event.metaKey || event.ctrlKey);
                  openDevice(suggestionRows[index].id, listIndex(index), newTab);
                }}
              >
                <ComboboxInlineAnchor onClose={() => setSearchOpen(false)}>
                <ComboboxInput
                  id="device-search"
                  type="search"
                  showTrigger={false}
                  placeholder="Search devices…"
                  aria-label="Search devices"
                  aria-describedby="device-search-help"
                  onClick={() => setSearchOpen(true)}
                  onKeyDownCapture={(event) => {
                    if (searchOpen) return;
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                      event.preventDefault();
                      event.stopPropagation();
                      if (rows.length > 0) {
                        const next = Math.min(Math.max(selectedIndex + (event.key === 'ArrowDown' ? 1 : -1), 0), rows.length - 1);
                        setSelectedId(rows[next].id);
                      }
                    } else if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      if (resultsMatchInput && selectedRow) openDevice(selectedRow.id, listIndex(selectedIndex), event.metaKey || event.ctrlKey);
                    }
                  }}
                  title="Search devices by serial, ID, description, owner, email, location or path"
                  className="w-full"
                  autoFocus
                />
                <ComboboxInlineContent open={searchOpen}>
                  <ComboboxEmpty className="flex">{error ? `Search failed: ${error}` : !resultsMatchInput ? 'Searching…' : 'No matching devices.'}</ComboboxEmpty>
                  <ComboboxList aria-label="Matching devices">
                    {(id: string) => {
                      const row = suggestionRows.find((candidate) => String(candidate.id) === id);
                      if (!row) return null;
                      return (
                        <ComboboxItem key={id} value={id} disabled={!resultsMatchInput}>
                          <span className="truncate">{searchRowClipLabel(row)} ({id})</span>
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxList>
                </ComboboxInlineContent>
                </ComboboxInlineAnchor>
              </Combobox>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFilters.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {activeFilters.map((f) => (
                <span
                  key={f.key as string}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-status-info-border bg-status-info text-status-info-foreground text-xs"
                >
                  {f.title}: {searchParams.get(f.key as string)}
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => setFilter(f.key, null)}
                    className="ml-1"
                    aria-label={`Clear ${f.title} filter`}
                    title={`Stop filtering the device list by ${f.title} and show every device again`}
                  >
                    ×
                  </Button>
                </span>
              ))}
            </div>
          )}

          {error && <p className="mt-4 text-status-error-foreground">{error}</p>}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Facets */}
            <aside className="lg:col-span-1 space-y-4">
              {FACET_FILTERS.map((f) => {
                const values = (facets[f.facet] as DeviceSearchFacet[]) ?? [];
                if (values.length === 0) return null;
                const active = searchParams.get(f.key as string);
                return (
                  <div key={f.facet}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {f.title}
                    </h3>
                    <ul className="space-y-0.5">
                      {values.slice(0, 8).map((v) => {
                        const isActive = active === v.value;
                        return (
                          <li key={v.value}>
                            <Button
                              variant={isActive ? 'primary' : 'secondary'}
                              onClick={() => setFilter(f.key, isActive ? null : v.value)}
                              title={
                                isActive
                                  ? `Stop filtering the device list by ${f.title}`
                                  : `Show only the devices whose ${f.title} is ${v.label || v.value}`
                              }
                              className="w-full justify-between gap-2"
                            >
                              <span className="truncate">{v.label || v.value}</span>
                              <span className="text-muted-foreground shrink-0">{v.count}</span>
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </aside>

            {/* Results */}
            <div className="min-w-0 lg:col-span-3">
              {loading && rows.length === 0 && <p className="text-muted-foreground">Searching…</p>}

              {!loading && rows.length === 0 && !error && (
                <p className="text-muted-foreground">
                  No devices match. Try clearing filters or a different search.
                </p>
              )}

              {rows.length > 0 && (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          {/* Matches the selection accent on the body cells so
                              the column stays aligned whatever is highlighted. */}
                          <th className="py-2 pr-2 pl-1 border-l-4 border-transparent font-medium">Device</th>
                          <th className="py-2 pr-2 font-medium" aria-sort={activatedAriaSort}>
                            <Button
                              variant="secondary"
                              onClick={toggleActivatedSort}
                              className={`whitespace-nowrap ${activatedSorted ? 'text-primary' : ''}`}
                              title="Sort by activation date (device first came online; not affected by filter changes)"
                              aria-label={activatedSortLabel}
                            >
                              Activated
                              <span aria-hidden className="text-xs">
                                {activatedSorted ? (dir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            </Button>
                          </th>
                          <th className="py-2 pr-2 font-medium">Responsibility</th>
                          <th className="py-2 pr-2 font-medium">MyFluxHive owner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((d, rowIndex) => (
                          <tr
                            key={d.id}
                            id={rowDomId(d.id)}
                            onClick={(e) => openDevice(d.id, listIndex(rowIndex), e.metaKey || e.ctrlKey || e.button === 1)}
                            onAuxClick={(e) => { if (e.button === 1) openDevice(d.id, listIndex(rowIndex), true); }}
                            // FS-633: the keyboard highlight needs a style of
                            // its own — the pointer is usually somewhere else,
                            // so the hover tint cannot stand in for it. The
                            // accent bar is a cell border rather than a ring on
                            // the row, because a collapsed table does not paint
                            // box-shadow on a <tr>.
                            aria-selected={rowIndex === selectedIndex}
                            className={`border-b last:border-0 cursor-pointer hover:bg-blue-50 ${
                              rowIndex === selectedIndex ? 'bg-blue-100' : ''
                            }`}
                          >
                            <td
                              className={`py-2 pr-2 pl-1 border-l-4 ${
                                rowIndex === selectedIndex ? 'border-blue-600' : 'border-transparent'
                              }`}
                            >
                              <div className="font-medium flex items-center gap-2">
                                <span className={d.decommissioned ? 'line-through text-muted-foreground' : ''}>
                                  {searchRowClipLabel(d)}
                                </span>
                                <DecommissionedBadge decommissioned={d.decommissioned} />
                              </div>
                              {/* The serial and its id read as one identifier,
                                  so this line never wraps — break-all split a
                                  24-digit Clip serial mid-id ("…3420 (21" /
                                  "3)"). A narrow viewport scrolls instead: the
                                  table sits in an overflow-x-auto wrapper. */}
                              <div className="text-foreground font-mono text-xs whitespace-nowrap">
                                {d.name} ({d.id})
                              </div>
                            </td>
                            <td className="py-2 pr-2 whitespace-nowrap tabular-nums text-gray-700">
                              {d.activated_at || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="py-2 pr-2 font-mono text-xs break-all">
                              {d.entity_path || '—'}
                            </td>
                            <td className="py-2 pr-2">
                              {d.app_context.status === 'missing' ? (
                                <span className="text-foreground">Not registered</span>
                              ) : (
                                <span>
                                  {d.app_context.owner_email || d.app_context.owner_name || 'Unknown'}
                                  {d.app_context.location_name && (
                                    <span className="text-muted-foreground"> · {d.app_context.location_name}</span>
                                  )}
                                  {d.app_context.status === 'ambiguous' && (
                                    <span className="ml-1 text-status-warning-foreground" title="Duplicate app registrations">⚠</span>
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paging controls */}
                  <div className="mt-4 flex items-center justify-between gap-4 flex-wrap text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <span>{first}–{last} of {total}</span>
                      <div className="ml-3 flex items-center gap-1">
                        <label htmlFor="device-index-page-size">Per page:</label>
                        <Select value={String(pageSize)} onValueChange={(next) => { if (next) setParam('page_size', next); }}>
                          <SelectTrigger id="device-index-page-size" aria-label="Per page: devices" title="Choose how many devices to show on each page">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <PageBtn label="« First" title="Go to the first page of matching devices" disabled={page <= 1} onClick={() => goToPage(1)} />
                      <PageBtn label="‹ Prev" title="Go back one page of matching devices" disabled={page <= 1} onClick={() => goToPage(page - 1)} />
                      <span className="px-2 text-gray-600">Page {page} of {pageCount}</span>
                      <PageBtn label="Next ›" title="Go forward one page of matching devices" disabled={page >= pageCount} onClick={() => goToPage(page + 1)} />
                      <PageBtn label="Last »" title="Go to the last page of matching devices" disabled={page >= pageCount} onClick={() => goToPage(pageCount)} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PageBtn: React.FC<{ label: string; title: string; disabled: boolean; onClick: () => void }> = ({
  label,
  title,
  disabled,
  onClick,
}) => (
  <Button variant="secondary" size="sm" onClick={onClick} disabled={disabled} title={title}>
    {label}
  </Button>
);

export default DeviceIndex;
